import React, { useEffect, useState } from "react";
import * as Styles from "./report.style";
import { Card, Col, Row } from "react-bootstrap";
import { Box, Button, ButtonGroup, Tab, Tabs } from "@mui/material";
import { colors } from "../../constants/colors";
import moment from "moment";
import { useTranslation } from "react-i18next";
import InputDatePicker, {
  PICKER_VIEWS,
} from "../../component/input/inputDatePicker";
import ButtonCustom from "../../component/button/buttonCustom";
import { icons } from "../../constants/images";
import ContractReport from "./components/Contract";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import ReportApi from "../../api/reports/reports.api";
import { getFileExtension } from "../../utils/fileTypeDownload";
import { saveAs } from "file-saver";
import EmptySpaceReport from "./components/EmptySpace";
import RevenueCollectionReport from "./components/RevenueCollection";
import WaterElectricBillReport from "./components/WaterElectricBill";
import Loading from "../../component/loading/loading";
import { formatThaiMonthYear, getBranch } from "../../utils/app.utils";
import ZoneApi from "../../api/zone/zone.api";
import AutocompleteSelect from "../../component/select/autoCompleteSelect";
import PaymentListReport from "./components/PaymentList";

const ReportContainer = () => {
  const { t } = useTranslation();
  const branchId: any = getBranch() ? Number(getBranch()) : undefined

  const [url, setUrl] = useState<string>("");
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");

  const [tabsValue, setTabsValue] = useState<string>("CONTRACT");
  const [searchType, setSearchType] = useState<"DATE" | "MONTH" | "YEAR">(
    "DATE"
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [zoneId, setZoneId] = useState<number>();
  const [zoneOption, setZoneOption] = useState<any[]>([]);
  const onChangeTabs = (event: React.SyntheticEvent, newValue: string) => {
    setTabsValue(newValue);
    setZoneId(undefined)
    setUrl('')
  };

  const handleDateChange = (type: "DATE" | "MONTH" | "YEAR") => {
    const date = new Date();

    switch (type) {
      case "DATE":
        const startOfDay = moment(date).startOf("day").format("YYYY-MM-DD");
        const endOfDay = moment(date)
          .startOf("day")
          .add(30, "days")
          .format("YYYY-MM-DD");
        setDateStart(startOfDay);
        setDateEnd(endOfDay);
        break;

      case "MONTH":
        // For "MONTH", add 1 month
        const startOfMonth = moment(date).startOf("month").format("YYYY-MM-DD");
        const endOfMonth = moment(date)
          .endOf("month")
          .add(1, "month")
          .format("YYYY-MM-DD");
        setDateStart(startOfMonth);
        setDateEnd(endOfMonth);
        break;

      case "YEAR":
        // For "YEAR", use the current year start and end
        const startOfYear = moment(date).startOf("year").format("YYYY-MM-DD");
        const endOfYear = moment(date).endOf("year").format("YYYY-MM-DD");
        setDateStart(startOfYear);
        setDateEnd(endOfYear);
        break;

      default:
        break;
    }
  };

  const getZone = async () => {
    const condition: any = {
      page: 1,
      pageLimit: 1000,
      status: "CONTRACT",
      branchId: branchId,
      skipcalculation: 0,
    };
    const res = await ZoneApi.findAll(condition);

    if (res.status === 200) {
      setZoneOption(res.data);
    } else {
      setZoneOption([]);
    }
  };

  useEffect(() => {
    getZone();
  }, []);
  useEffect(() => {
    handleDateChange(searchType);
  }, [searchType]);

  const handleDownloadFile = async (
    start_date: string,
    end_date: string,
    report_id: string,
    searh_type: string,
    download_type: string,
    zoneId?: any,
    branchId?: number
  ) => {
    if (report_id) {
      try {
        setLoading(true);
        let res = null as any;
        const query = {
          start_date: start_date,
          end_date: end_date,
          type: searh_type,
          type_doc: download_type ?? "pdf",
          zoneId: zoneId
        };

        if (report_id === "CONTRACT") {
          res = await ReportApi.downloadAgreement(query);
        } else if (report_id === "EMPTY_SPACE") {
          res = await ReportApi.downloadBankContract(query);
        } else if (report_id === "REVENUE_COLLECTION") {
          res = await ReportApi.downloadRevenueCollection({
            ...query,
            type: searchType ? String(searchType).toLowerCase() : undefined
          });
        } else if (report_id === "WATER_ELECTRIC_BILL") {
          res = await ReportApi.downloadUtilities(query);
        } else if (report_id === "PAYMENT_LIST") {
          res = await ReportApi.downloadPaymentList(query);
        } else if (report_id === "INDIVIDUAL_DEBTOR") {
          res = await ReportApi.downloadIndividualDebtor(query);
        } else if (report_id === "SALES_TAX_REPORT" && branchId) {
          res = await ReportApi.downloadSaleTax({
            branchId: branchId,
            date: moment(start_date).format("YYYY-MM-DD")
          });
        }
        else {
          console.error("Error fetching report not macth id");
        }

        if (res.status < 300) {
          let file_name_fix: string = "";
          const prefix_month = formatThaiMonthYear(start_date);
          const response = res.data;
          if (branchId && report_id === "SALES_TAX_REPORT") {
            file_name_fix = "รายงานภาษีขาย"
          }

          // Get the content type from the server's response headers
          const contentType = res.headers["content-type"];

          // Create a Blob with the content type from the response
          const blob = new Blob([response], { type: contentType });
          if (download_type === 'pdf') {
            setUrl(URL.createObjectURL(blob))
          }
          // Extract file extension based on content type
          const fileExtension = getFileExtension(contentType || "");

          // Generate a filename with the correct extension (you can also modify this to use a custom name)
          const name_file = (file_name_fix && prefix_month) ? `${file_name_fix}_${prefix_month}` : `${report_id}_${new Date().getTime()}`;
          const fileName = `${name_file}.${fileExtension}`;

          // Save the file
          saveAs(blob, fileName);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching report:", error);
        setLoading(false);
      }
    } else {
      console.error(`No API function mapped for id: ${report_id}`);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <Loading show={loading} type="fullLoading" />
        </div>
      )}

      <Styles.MainContainer>
        <Styles.PageContainer>
          <Card.Body className="px-5">
            <Tabs
              value={tabsValue}
              onChange={onChangeTabs}
              TabIndicatorProps={{ sx: { backgroundColor: "transparent" } }}
              variant="scrollable"
              sx={{
                borderBottom: `1px solid ${colors.lightGray}`,
                "& button": {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  backgroundColor: colors.lightGray,
                  marginRight: 1,
                  minHeight: "41px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                },
                "& button:focus": {
                  backgroundColor: colors.themeMainColor,
                  color: colors.white,
                  // height: 40,
                },
              }}
            >
              <Tab
                value="CONTRACT"
                onClick={() => { setTabsValue("CONTRACT") }}
                iconPosition="start"
                label={t("รายงานสัญญา")}
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />
              <Tab
                value="EMPTY_SPACE"
                onClick={() => setTabsValue("EMPTY_SPACE")}
                iconPosition="start"
                label="รายงานพื้นที่ว่าง"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />
              <Tab
                value="REVENUE_COLLECTION"
                onClick={() => setTabsValue("REVENUE_COLLECTION")}
                iconPosition="start"
                label="รายงานการจัดเก็บรายได้"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />
              <Tab
                value="WATER_ELECTRIC_BILL"
                onClick={() => setTabsValue("WATER_ELECTRIC_BILL")}
                iconPosition="start"
                label="รายการค่าน้ำค่าไฟ"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />
              <Tab
                value="PAYMENT_LIST"
                onClick={() => {
                  setTabsValue("PAYMENT_LIST");
                  setSearchType("DATE")
                }}
                iconPosition="start"
                label="รายการชำระเงิน"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />

              
              {/* <Tab
                value="INDIVIDUAL_DEBTOR"
                onClick={() => setTabsValue("INDIVIDUAL_DEBTOR")}
                iconPosition="start"
                label="รายงานลูกหนี้รายบุคคล"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              /> */}

              {/*  รายงานภาษีขาย */}
              <Tab
                value="SALES_TAX_REPORT"
                onClick={() => {
                  setTabsValue("SALES_TAX_REPORT");
                  setSearchType("MONTH")
                }}
                iconPosition="start"
                label="รายงานภาษีขาย"
                sx={{
                  "&.Mui-selected": {
                    color: colors.white,
                    backgroundColor: colors.themeMainColor,
                  },
                }}
              />
            </Tabs>

            {/* แท็บ PAYMENT_LIST มีตัวกรอง + ปุ่ม Excel ของตัวเองใน components/PaymentList.tsx
                (ตัวกรองชุดนั้นคนละชุดกับแถวนี้) จึงซ่อนแถวที่ใช้ร่วมกันตอนอยู่แท็บนั้น
                เงื่อนไขเดิมที่อ้าง PAYMENT_LIST ข้างในยังอยู่ครบ ไม่ได้แตะ เพื่อไม่ให้กระทบแท็บอื่น */}
            {tabsValue !== "PAYMENT_LIST" && (
            <Row>
              {
                !['PAYMENT_LIST', 'SALES_TAX_REPORT'].includes(tabsValue) ?
                  <Col className="my-2" lg={2} md={12} xs={12}>
                    <ButtonGroup
                      variant="outlined"
                      aria-label="Basic button group"
                      sx={{
                        marginTop: "4px",
                      }}
                    >
                      <Button
                        onClick={() => setSearchType("DATE")}
                        variant={searchType === "DATE" ? "contained" : "outlined"}
                        sx={{
                          width: "75px",
                        }}
                      >
                        วัน
                      </Button>
                      <Button
                        onClick={() => setSearchType("MONTH")}
                        variant={searchType === "MONTH" ? "contained" : "outlined"}
                        sx={{
                          width: "75px",
                        }}
                      >
                        เดือน
                      </Button>
                      <Button
                        onClick={() => setSearchType("YEAR")}
                        variant={searchType === "YEAR" ? "contained" : "outlined"}
                        sx={{
                          width: "75px",
                        }}
                      >
                        ปี
                      </Button>
                    </ButtonGroup>
                  </Col>
                  :
                  null
              }
              <Col className="my-2" lg={3} md={6} sm={12} xs={12}>
                {searchType === "DATE" ? (
                  <InputDatePicker
                    required
                    disableFuture
                    value={dateStart}
                    allowClear
                    onClear={() => setDateStart("")}
                    key={"date"}
                    onChange={(e: any) => {
                      setDateStart(moment(e).format("YYYY-MM-DD"));
                    }}
                    dateFormat="YYYY-MM-DD"
                  />
                ) : searchType === "MONTH" ? (
                  <InputDatePicker
                    value={dateStart}
                    allowClear
                    onClear={() => setDateStart("")}
                    key={"date"}
                    onChange={(e: any) => {
                      // console.log("e", e);
                      setDateStart(moment(e).format("YYYY-MM-DD"));
                    }}
                    views={[PICKER_VIEWS.MONTH, PICKER_VIEWS.YEAR]}
                    dateFormat="YYYY-MM"
                  />
                ) : searchType === "YEAR" ? (
                  <InputDatePicker
                    value={dateStart}
                    allowClear
                    onClear={() => setDateStart("")}
                    key={"date"}
                    onChange={(e: any) => {
                      setDateStart(moment(e).format("YYYY-MM-DD"));
                    }}
                    views={[PICKER_VIEWS.YEAR]}
                    dateFormat="YYYY"
                  />
                ) : null}
              </Col>
              {!['SALES_TAX_REPORT'].includes(tabsValue) && (
                <>
                  <Col className="my-2 text-center" lg={1} md={1} sm={12} xs={12}>
                    <p
                      style={{
                        marginTop: "12px",
                      }}
                    >
                      หา
                    </p>
                  </Col>
                  <Col className="my-2" lg={3} md={6} sm={12} xs={12}>
                    {searchType === "DATE" ? (
                      <InputDatePicker
                        required
                        disableStartDate={dateStart}
                        value={dateEnd}
                        allowClear
                        onClear={() => setDateEnd("")}
                        key={"date"}
                        onChange={(e: any) => {
                          setDateEnd(moment(e).format("YYYY-MM-DD"));
                        }}
                        dateFormat="YYYY-MM-DD"
                      />
                    ) : searchType === "MONTH" ? (
                      <InputDatePicker
                        value={dateEnd}
                        allowClear
                        onClear={() => setDateEnd("")}
                        key={"date"}
                        onChange={(e: any) => {
                          setDateEnd(moment(e).format("YYYY-MM-DD"));
                        }}
                        views={[PICKER_VIEWS.MONTH, PICKER_VIEWS.YEAR]}
                        dateFormat="YYYY-MM"
                      />
                    ) : searchType === "YEAR" ? (
                      <InputDatePicker
                        value={dateEnd}
                        allowClear
                        onClear={() => setDateEnd("")}
                        key={"date"}
                        onChange={(e: any) => {
                          setDateEnd(moment(e).format("YYYY-MM-DD"));
                        }}
                        views={[PICKER_VIEWS.YEAR]}
                        dateFormat="YYYY"
                      />
                    ) : null}
                  </Col>
                </>
              )}


              {tabsValue === "PAYMENT_LIST" &&
                <Col className="my-2" lg={3} md={6} sm={12} xs={12}>
                  <AutocompleteSelect
                    options={zoneOption || []}
                    getOptionLabel={(option: any) => option.zoneName}
                    onChange={(e: any, val: any) => {
                      setZoneId(val?.zoneId);
                    }}
                    labelId="zone-name"
                    placeholder={t("SUE.FILTER.SEARCH_ZONE")}
                  />
                </Col>}
              <Col className="my-2" lg={tabsValue === "PAYMENT_LIST" ? 2 : 3} md={6} sm={12} xs={12}>
                <Row>
                  <Col lg={6} md={6} sm={6} xs={6}>
                    <ButtonCustom
                      onClick={() =>
                        handleDownloadFile(
                          dateStart,
                          dateEnd,
                          tabsValue,
                          searchType,
                          "xlsx",
                          zoneId,
                          branchId
                        )
                      }
                      textButton={t("Excel")}
                      style={{
                        width: "100%",
                        "&.MuiButton-contained": {
                          // backgroundColor:  `${roleId !== 4 ? '#5685f5' : 'red'} !important`,
                          backgroundColor: `${colors.lightOrange} !important`,
                          ":hover": {
                            // backgroundColor: `${roleId !== 4 ? '#5685f5' : 'red'} !important`,
                            backgroundColor: `${colors.lightOrange} !important`,
                          },
                        },
                      }}
                      startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                    />
                  </Col>
                  {
                    !['SALES_TAX_REPORT', 'PAYMENT_LIST'].includes(tabsValue)
                      ?
                      <Col lg={6} md={6} sm={6} xs={6}>
                        <ButtonCustom
                          style={{ width: "100%" }}
                          type="button"
                          textButton={t("SYSTEM.BUTTON.PRINT")}
                          endIcon={<img src={icons.print} />}
                          onClick={() =>
                            handleDownloadFile(
                              dateStart,
                              dateEnd,
                              tabsValue,
                              searchType,
                              "pdf"
                            )
                          }
                        />
                      </Col>
                      : null
                  }
                </Row>
              </Col>
            </Row>
            )}

            {/* <InputTextField
                  placeholder={t("SUE.FILTER.SEARCH")}
                  label={t("SUE.FILTER.SEARCH")}
                  onchange={(event) => {

                  }}
                  type="month"
                /> */}
            {tabsValue === "CONTRACT" && (
              <ContractReport
                date_start={dateStart}
                date_end={dateEnd}
                search_type={searchType}
              />
            )}
            {tabsValue === "EMPTY_SPACE" && (
              <EmptySpaceReport
                date_start={dateStart}
                date_end={dateEnd}
                search_type={searchType}
              />
            )}
            {tabsValue === "REVENUE_COLLECTION" && (
              <RevenueCollectionReport
                date_start={dateStart}
                date_end={dateEnd}
                search_type={searchType}
              />
            )}
            {tabsValue === "WATER_ELECTRIC_BILL" && (
              <WaterElectricBillReport
                date_start={dateStart}
                date_end={dateEnd}
                search_type={searchType}
              />
            )}
            {/* ไม่ส่ง props — self-contained ถือตัวกรอง/ตาราง/pagination/Excel ของตัวเอง */}
            {tabsValue === "PAYMENT_LIST" && <PaymentListReport />}
            {tabsValue === "INDIVIDUAL_DEBTOR" && (
              <div style={{ width: "100%", height: "70vh", marginTop: "" }}>
                {url && <iframe
                  src={`/pdfjs-dist/web/viewer.html?file=${url}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                />}
              </div>
            )}
          </Card.Body>
        </Styles.PageContainer>
      </Styles.MainContainer>
    </>
  );
};

export default ReportContainer;
